import { BusinessObjectBase } from './base';
import type { ClassEnrollment } from './class-enrollment';
import type { User } from './user';
import type { Venue } from './venue';

export interface TrainingClassData {
  readonly id: number;
  readonly name?: string;
  readonly startDate?: string;
  readonly endDate?: string;
  readonly venueId: number;
  readonly venue?: Venue;
  readonly instructorId: number;
  readonly instructor?: User;
  readonly classEnrollments?: ClassEnrollment[];
}

export interface TrainingClass extends Readonly<TrainingClassData> {}
export class TrainingClass extends BusinessObjectBase {
  static readonly entityName = 'TrainingClass' as const;
  static readonly plural = 'training-classes';

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<TrainingClass[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new TrainingClass(data));
  }

  static async findOne(filter?: any): Promise<TrainingClass | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new TrainingClass(data) : null;
  }

  static async findById(id: number, filter?: any): Promise<TrainingClass> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new TrainingClass(data);
  }

  static async create(
    data: Omit<TrainingClassData, 'id'>,
  ): Promise<TrainingClass> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new TrainingClass(result);
  }

  static async updateById(
    id: number,
    data: Partial<TrainingClassData>,
  ): Promise<TrainingClass> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const result = await this.patch<any>(url, data);
    return new TrainingClass(result);
  }

  static async deleteById(id: number): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
